import React, { useEffect, useRef, useState } from 'react';
import { SessionData } from '../utils/SessionTracker';
import { formatTime } from '../utils/formatTime';
import type { ReplayFrame } from '../utils/ReplayRecorder';

interface SessionSummaryProps {
  sessionData: SessionData;
  onNewSession: () => void;
  replayFrames?: ReplayFrame[] | null;
  pipelineStats?: {
    leftPct: number;
    rightPct: number;
    meanPeakHeight: number;
    totalThrows: number;
    totalCatches: number;
  } | null;
  videoDimensions?: { width: number; height: number };
}

export const SessionSummary = ({ sessionData, onNewSession, replayFrames, pipelineStats, videoDimensions = { width: 640, height: 480 } }: SessionSummaryProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [scrubPosition, setScrubPosition] = useState(0);
  const requestRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const firstTs = replayFrames?.[0]?.timestampMs ?? 0;
  const lastTs = replayFrames?.[replayFrames.length - 1]?.timestampMs ?? 0;
  const duration = lastTs - firstTs || 1;

  const drawFrame = (tNorm: number) => {
    if (!canvasRef.current || !replayFrames || replayFrames.length === 0) return;
    const targetTs = firstTs + tNorm * duration;
    let frameIndex = replayFrames.findIndex((f) => f.timestampMs >= targetTs);
    if (frameIndex < 0) frameIndex = replayFrames.length - 1;
    const frame = replayFrames[frameIndex];
    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;
    const { width, height } = canvasRef.current;
    ctx.clearRect(0, 0, width, height);
    const colors: ReadonlyArray<string> = ['#ef4444', '#3b82f6', '#eab308'];
    for (const ball of frame.balls) {
      const color = colors[(ball.id - 1) % colors.length] ?? '#22c55e';
      const x = (ball.x / videoDimensions.width) * width;
      const y = (ball.y / videoDimensions.height) * height;
      ctx.beginPath();
      ctx.arc(x, y, 8, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
    }
  };

  useEffect(() => {
    if (!isPlaying || !replayFrames || replayFrames.length === 0) return;
    const draw = (now: number) => {
      if (!canvasRef.current) return;
      if (startTimeRef.current == null) startTimeRef.current = now;
      const elapsed = (now - startTimeRef.current) * playbackSpeed;
      const tNorm = Math.min(elapsed / duration, 1);
      setScrubPosition(tNorm);
      drawFrame(tNorm);
      if (tNorm < 1) {
        requestRef.current = requestAnimationFrame(draw);
      } else {
        setIsPlaying(false);
        startTimeRef.current = null;
      }
    };
    requestRef.current = requestAnimationFrame(draw);
    return () => {
      if (requestRef.current != null) cancelAnimationFrame(requestRef.current);
    };
    // drawFrame uses replayFrames, videoDimensions which are in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, replayFrames, playbackSpeed, duration, videoDimensions]);

  useEffect(() => () => {
    if (requestRef.current != null) cancelAnimationFrame(requestRef.current);
  }, []);

  const handlePlayPause = () => {
    if (!replayFrames || replayFrames.length === 0) return;
    if (isPlaying) {
      if (requestRef.current != null) cancelAnimationFrame(requestRef.current);
      requestRef.current = null;
      setIsPlaying(false);
    } else {
      startTimeRef.current = null;
      setIsPlaying(true);
    }
  };

  const handleScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    setScrubPosition(v);
    drawFrame(v);
  };

  return (
    <div className="py-10 px-6 text-center max-w-md mx-auto">
      <h2 className="text-2xl font-semibold text-gray-900">
        {sessionData.goalReached ? '🎉 Goal Reached!' : 'Session Complete'}
      </h2>
      <div className="text-2xl mt-8 space-y-4">
        <div>
          <strong>Total Throws:</strong> {sessionData.consecutiveThrows}
        </div>
        <div>
          <strong>Time Juggled:</strong> {formatTime(sessionData.cumulativeTime)}
        </div>
        {pipelineStats != null && (
          <>
            <div className="text-lg">
              Left/Right: {pipelineStats.leftPct.toFixed(0)}% / {pipelineStats.rightPct.toFixed(0)}%
            </div>
            <div className="text-lg">
              Catches: {pipelineStats.totalCatches}
            </div>
          </>
        )}
        {sessionData.throwsGoal != null && (
          <div className="text-gray-600">Goal: {sessionData.throwsGoal} throws</div>
        )}
        {sessionData.timeGoal != null && (
          <div className="text-gray-600">Goal: {formatTime(sessionData.timeGoal)}</div>
        )}
      </div>

      {replayFrames && replayFrames.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Ball Path Replay</h3>
          <canvas
            ref={canvasRef}
            width={320}
            height={240}
            className="border border-gray-300 rounded bg-black mx-auto block"
          />
          <div className="mt-3 space-y-2">
            <input
              type="range"
              min={0}
              max={1}
              step={0.01}
              value={scrubPosition}
              onChange={handleScrub}
              className="w-full"
              aria-label="Scrub timeline"
            />
            <div className="flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={handlePlayPause}
                className="px-4 py-2 text-base cursor-pointer bg-blue-600 text-white border-0 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {isPlaying ? 'Pause' : 'Play'}
              </button>
              <select
                value={playbackSpeed}
                onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
                className="px-2 py-1 border border-gray-300 rounded text-sm"
                aria-label="Playback speed"
              >
                <option value={0.25}>0.25×</option>
                <option value={0.5}>0.5×</option>
                <option value={1}>1×</option>
                <option value={1.5}>1.5×</option>
                <option value={2}>2×</option>
              </select>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={onNewSession}
        className="mt-8 px-6 py-3 text-lg cursor-pointer bg-green-600 text-white border-0 rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
      >
        New Session
      </button>
    </div>
  );
}

